// ─── GBCOSPACK — Quotation Template Studio Fixtures ──────────────────────────
// Design-only mock data.  NOT a pricing authority.  NOT for production use.
// Each fixture is available in en / ko / zh-CN locale variants.

import type { QuotationDocumentData, DocumentLocale, DocumentType } from '@/quotation/document/types';

// ─── Shared seller (locale-invariant) ────────────────────────────────────────
const SELLER = {
  companyName: 'GBCOSPACK',
  legalName: 'GB Cosmetic Packaging Co., Ltd.',
  address: '12F, Gangnam Finance Center, 152 Teheran-ro, Gangnam-gu, Seoul',
  country: 'Republic of Korea',
  telephone: '+82-2-1234-5678',
  email: 'trade@gbcospack.com',
  website: 'www.gbcospack.com',
};

const BUYER_EN = {
  companyName: 'LUMIÈRE Cosmetics GmbH',
  contactName: 'Sarah Müller',
  address: 'Industriestraße 48, 60314 Frankfurt am Main',
  country: 'Germany',
  email: 's.mueller@lumiere-cosmetics.de',
  phone: '+49-69-1234-5678',
};

const BUYER_KO = {
  companyName: '아모레퍼시픽 주식회사',
  contactName: '김지수 팀장',
  address: '서울특별시 용산구 한강대로 100',
  country: '대한민국',
  email: 'jisoo.kim@amorepacific.com',
  phone: '+82-2-6040-1234',
};

const BUYER_ZH = {
  companyName: '上海美丽都化妆品有限公司',
  contactName: '陈梅',
  address: '上海市浦东新区张江高科技园区博云路2号',
  country: '中国',
  email: 'chen.mei@meilidu.com.cn',
  phone: '+86-21-6899-5678',
};

function pqNum(s: string) { return `PQ-2026-${s}`; }
function foNum(s: string) { return `FO-2026-${s}`; }

// ============================================================================
// STANDARD — 3 products
// ============================================================================
function buildStandard(locale: DocumentLocale, docType: DocumentType): QuotationDocumentData {
  const buyer = locale === 'ko' ? BUYER_KO : locale === 'zh-CN' ? BUYER_ZH : BUYER_EN;

  type ItemDef = {
    id: string; sku: string; productName: string;
    categoryL1Display: string; categoryL2Display: string;
    materialDisplay: string; capacity: string; specification: string;
    productImageUrl: string; quantity: number; unitPrice: number; amount: number;
    finishing: { displayName: string; color?: string; pantone?: string; unitPrice: number; amount: number }[];
  };

  const items_en: ItemDef[] = [
    {
      id: 'std-1', sku: 'GB-AB-30-PT',
      productName: 'Airless Pump Bottle 30 ml',
      categoryL1Display: 'Airless Packaging', categoryL2Display: 'Pump Bottle',
      materialDisplay: 'PETG / PP', capacity: '30 ml', specification: 'Ø30 × 95 mm, Full White + Clear Window',
      productImageUrl: 'https://readdy.ai/api/search-image?query=Elegant%20transparent%20airless%20pump%20bottle%2030ml%20cosmetic%20packaging%20isolated%20on%20pure%20white%20background%20minimal%20product%20photography%20studio%20lighting%20symmetrical%20composition%20commercial%20catalog%20style&width=200&height=200&seq=fix-std-en-1&orientation=squarish',
      quantity: 5000, unitPrice: 0.67, amount: 3350.00,
      finishing: [
        { displayName: 'Silk Screen Printing', color: 'Black', pantone: 'PANTONE Black C', unitPrice: 0.07, amount: 350.00 },
        { displayName: 'Hot Stamping', color: 'Gold', unitPrice: 0.11, amount: 550.00 },
      ],
    },
    {
      id: 'std-2', sku: 'GB-LB-50-FR',
      productName: 'Lotion Pump Bottle 50 ml',
      categoryL1Display: 'Lotion Packaging', categoryL2Display: 'Pump Bottle',
      materialDisplay: 'PP / AS', capacity: '50 ml', specification: 'Ø32 × 118 mm, Frosted White',
      productImageUrl: 'https://readdy.ai/api/search-image?query=Modern%20frosted%20white%20lotion%20pump%20bottle%2050ml%20cosmetic%20container%20clean%20minimal%20studio%20product%20photography%20pure%20white%20background%20gentle%20shadow%20luxury%20skincare%20packaging%20centered%20composition&width=200&height=200&seq=fix-std-en-2&orientation=squarish',
      quantity: 3000, unitPrice: 0.82, amount: 2460.00,
      finishing: [
        { displayName: 'UV Coating', unitPrice: 0.09, amount: 270.00 },
      ],
    },
    {
      id: 'std-3', sku: 'GB-JR-50-GL',
      productName: 'Cream Jar 50 ml',
      categoryL1Display: 'Jar Packaging', categoryL2Display: 'Wide Mouth Jar',
      materialDisplay: 'PMMA / PP', capacity: '50 ml', specification: 'Ø60 × 45 mm, Glossy Black',
      productImageUrl: 'https://readdy.ai/api/search-image?query=Luxury%20glossy%20black%20cream%20jar%2050ml%20cosmetic%20packaging%20isolated%20on%20clean%20white%20background%20professional%20product%20photography%20minimal%20symmetrical%20view%20from%20above%20slight%20angle&width=200&height=200&seq=fix-std-en-3&orientation=squarish',
      quantity: 2000, unitPrice: 0.95, amount: 1900.00,
      finishing: [
        { displayName: 'Silk Screen Printing', color: 'Silver', pantone: 'PANTONE 877 C', unitPrice: 0.08, amount: 160.00 },
      ],
    },
  ];

  const items_ko: ItemDef[] = [
    {
      id: 'std-1', sku: 'GB-AB-30-PT',
      productName: '에어리스 펌프 보틀 30ml',
      categoryL1Display: '에어리스 패키징', categoryL2Display: '펌프 보틀',
      materialDisplay: 'PETG / PP', capacity: '30 ml', specification: '직경 30 × 높이 95 mm, 풀 화이트 + 투명 창',
      productImageUrl: 'https://readdy.ai/api/search-image?query=Elegant%20transparent%20airless%20pump%20bottle%2030ml%20cosmetic%20packaging%20isolated%20on%20pure%20white%20background%20minimal%20product%20photography%20studio%20lighting%20symmetrical%20composition%20commercial%20catalog%20style&width=200&height=200&seq=fix-std-ko-1&orientation=squarish',
      quantity: 5000, unitPrice: 0.67, amount: 3350.00,
      finishing: [
        { displayName: '실크 스크린 인쇄', color: '블랙', pantone: 'PANTONE Black C', unitPrice: 0.07, amount: 350.00 },
        { displayName: '핫 스탬핑', color: '골드', unitPrice: 0.11, amount: 550.00 },
      ],
    },
    {
      id: 'std-2', sku: 'GB-LB-50-FR',
      productName: '로션 펌프 보틀 50ml',
      categoryL1Display: '로션 패키징', categoryL2Display: '펌프 보틀',
      materialDisplay: 'PP / AS', capacity: '50 ml', specification: '직경 32 × 높이 118 mm, 프로스트 화이트',
      productImageUrl: 'https://readdy.ai/api/search-image?query=Modern%20frosted%20white%20lotion%20pump%20bottle%2050ml%20cosmetic%20container%20clean%20minimal%20studio%20product%20photography%20pure%20white%20background%20gentle%20shadow%20luxury%20skincare%20packaging%20centered%20composition&width=200&height=200&seq=fix-std-ko-2&orientation=squarish',
      quantity: 3000, unitPrice: 0.82, amount: 2460.00,
      finishing: [{ displayName: 'UV 코팅', unitPrice: 0.09, amount: 270.00 }],
    },
    {
      id: 'std-3', sku: 'GB-JR-50-GL',
      productName: '크림 자 50ml',
      categoryL1Display: '자 패키징', categoryL2Display: '와이드 자',
      materialDisplay: 'PMMA / PP', capacity: '50 ml', specification: '직경 60 × 높이 45 mm, 유광 블랙',
      productImageUrl: 'https://readdy.ai/api/search-image?query=Luxury%20glossy%20black%20cream%20jar%2050ml%20cosmetic%20packaging%20isolated%20on%20clean%20white%20background%20professional%20product%20photography%20minimal%20symmetrical%20view%20from%20above%20slight%20angle&width=200&height=200&seq=fix-std-ko-3&orientation=squarish',
      quantity: 2000, unitPrice: 0.95, amount: 1900.00,
      finishing: [{ displayName: '실크 스크린 인쇄', color: '실버', pantone: 'PANTONE 877 C', unitPrice: 0.08, amount: 160.00 }],
    },
  ];

  const items_zh: ItemDef[] = [
    {
      id: 'std-1', sku: 'GB-AB-30-PT',
      productName: '无泵真空瓶 30ml',
      categoryL1Display: '真空包装', categoryL2Display: '按压泵瓶',
      materialDisplay: 'PETG / PP', capacity: '30 ml', specification: '直径30 × 高95 mm，全白+透明窗',
      productImageUrl: 'https://readdy.ai/api/search-image?query=Elegant%20transparent%20airless%20pump%20bottle%2030ml%20cosmetic%20packaging%20isolated%20on%20pure%20white%20background%20minimal%20product%20photography%20studio%20lighting%20symmetrical%20composition%20commercial%20catalog%20style&width=200&height=200&seq=fix-std-zh-1&orientation=squarish',
      quantity: 5000, unitPrice: 0.67, amount: 3350.00,
      finishing: [
        { displayName: '丝网印刷', color: '黑色', pantone: 'PANTONE Black C', unitPrice: 0.07, amount: 350.00 },
        { displayName: '烫金', color: '金色', unitPrice: 0.11, amount: 550.00 },
      ],
    },
    {
      id: 'std-2', sku: 'GB-LB-50-FR',
      productName: '乳液泵瓶 50ml',
      categoryL1Display: '乳液包装', categoryL2Display: '泵式瓶',
      materialDisplay: 'PP / AS', capacity: '50 ml', specification: '直径32 × 高118 mm，磨砂白',
      productImageUrl: 'https://readdy.ai/api/search-image?query=Modern%20frosted%20white%20lotion%20pump%20bottle%2050ml%20cosmetic%20container%20clean%20minimal%20studio%20product%20photography%20pure%20white%20background%20gentle%20shadow%20luxury%20skincare%20packaging%20centered%20composition&width=200&height=200&seq=fix-std-zh-2&orientation=squarish',
      quantity: 3000, unitPrice: 0.82, amount: 2460.00,
      finishing: [{ displayName: 'UV上光', unitPrice: 0.09, amount: 270.00 }],
    },
    {
      id: 'std-3', sku: 'GB-JR-50-GL',
      productName: '面霜广口瓶 50ml',
      categoryL1Display: '广口瓶包装', categoryL2Display: '广口瓶',
      materialDisplay: 'PMMA / PP', capacity: '50 ml', specification: '直径60 × 高45 mm，亮光黑',
      productImageUrl: 'https://readdy.ai/api/search-image?query=Luxury%20glossy%20black%20cream%20jar%2050ml%20cosmetic%20packaging%20isolated%20on%20clean%20white%20background%20professional%20product%20photography%20minimal%20symmetrical%20view%20from%20above%20slight%20angle&width=200&height=200&seq=fix-std-zh-3&orientation=squarish',
      quantity: 2000, unitPrice: 0.95, amount: 1900.00,
      finishing: [{ displayName: '丝网印刷', color: '银色', pantone: 'PANTONE 877 C', unitPrice: 0.08, amount: 160.00 }],
    },
  ];

  const items = locale === 'ko' ? items_ko : locale === 'zh-CN' ? items_zh : items_en;
  const productAmount = items.reduce((s, i) => s + i.amount, 0);
  const finishingAmount = items.reduce((s, i) => s + i.finishing.reduce((fs, f) => fs + f.amount, 0), 0);
  const subtotal = productAmount + finishingAmount;
  const freightAmount = docType === 'final_offer' ? 480 : undefined;
  const total = subtotal + (freightAmount || 0);

  return {
    documentType: docType, documentLocale: locale,
    quotationNumber: docType === 'preliminary_quote' ? pqNum('000201') : foNum('000201'),
    referenceNumber: docType === 'final_offer' ? pqNum('000201') : undefined,
    issuedAt: '2026-08-27', validUntil: docType === 'final_offer' ? '2026-09-11' : undefined,
    seller: SELLER, buyer, items, currency: 'USD',
    productAmount, finishingAmount, subtotal, freightAmount, total,
    paymentTerms: docType === 'final_offer' ? '30% Deposit / 70% Before Shipment' : undefined,
    incoterm: 'FOB', namedPlace: 'Shanghai Port, China — Incoterms® 2020',
    leadTime: docType === 'final_offer' ? '25–30 days after final artwork approval' : undefined,
  };
}

// ============================================================================
// SINGLE PRODUCT
// ============================================================================
function buildSingleProduct(locale: DocumentLocale, docType: DocumentType): QuotationDocumentData {
  const buyer = locale === 'ko' ? BUYER_KO : locale === 'zh-CN' ? BUYER_ZH : BUYER_EN;

  type SingleItem = {
    id: string; sku: string; productName: string;
    categoryL1Display: string; categoryL2Display: string;
    materialDisplay: string; capacity: string; specification: string;
    productImageUrl: string; quantity: number; unitPrice: number; amount: number;
    finishing: { displayName: string; color?: string; unitPrice: number; amount: number }[];
  };

  const item_en: SingleItem = {
    id: 'sp-1', sku: 'GB-FB-100-AM',
    productName: 'Serum Dropper Bottle 100 ml',
    categoryL1Display: 'Dropper Packaging', categoryL2Display: 'Serum Bottle',
    materialDisplay: 'PETG / Aluminum', capacity: '100 ml', specification: 'Ø28 × 140 mm, Matte Silver Metal Cap',
    productImageUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20dropper%20bottle%20100ml%20cosmetic%20serum%20packaging%20matte%20silver%20aluminum%20cap%20clear%20PETG%20body%20isolated%20white%20background%20minimal%20product%20photography%20centered%20soft%20diffused%20lighting&width=200&height=200&seq=fix-sp-en-1&orientation=squarish',
    quantity: 10000, unitPrice: 1.15, amount: 11500.00,
    finishing: [{ displayName: 'Rose Gold Foil Stamping', color: 'Rose Gold', unitPrice: 0.14, amount: 1400.00 }],
  };
  const item_ko: SingleItem = {
    id: 'sp-1', sku: 'GB-FB-100-AM',
    productName: '세럼 드로퍼 보틀 100ml',
    categoryL1Display: '드로퍼 패키징', categoryL2Display: '세럼 보틀',
    materialDisplay: 'PETG / 알루미늄', capacity: '100 ml', specification: '직경 28 × 높이 140 mm, 무광 실버 메탈 캡',
    productImageUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20dropper%20bottle%20100ml%20cosmetic%20serum%20packaging%20matte%20silver%20aluminum%20cap%20clear%20PETG%20body%20isolated%20white%20background%20minimal%20product%20photography%20centered%20soft%20diffused%20lighting&width=200&height=200&seq=fix-sp-ko-1&orientation=squarish',
    quantity: 10000, unitPrice: 1.15, amount: 11500.00,
    finishing: [{ displayName: '로즈골드 호일 스탬핑', color: '로즈골드', unitPrice: 0.14, amount: 1400.00 }],
  };
  const item_zh: SingleItem = {
    id: 'sp-1', sku: 'GB-FB-100-AM',
    productName: '精华液滴管瓶 100ml',
    categoryL1Display: '滴管包装', categoryL2Display: '精华瓶',
    materialDisplay: 'PETG / 铝', capacity: '100 ml', specification: '直径28 × 高140 mm，哑光银色金属盖',
    productImageUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20dropper%20bottle%20100ml%20cosmetic%20serum%20packaging%20matte%20silver%20aluminum%20cap%20clear%20PETG%20body%20isolated%20white%20background%20minimal%20product%20photography%20centered%20soft%20diffused%20lighting&width=200&height=200&seq=fix-sp-zh-1&orientation=squarish',
    quantity: 10000, unitPrice: 1.15, amount: 11500.00,
    finishing: [{ displayName: '烫金标签', color: '玫瑰金', unitPrice: 0.14, amount: 1400.00 }],
  };

  const item = locale === 'ko' ? item_ko : locale === 'zh-CN' ? item_zh : item_en;
  const subtotal = item.amount + item.finishing[0].amount;
  const freightAmount = docType === 'final_offer' ? 620 : undefined;

  return {
    documentType: docType, documentLocale: locale,
    quotationNumber: docType === 'preliminary_quote' ? pqNum('000202') : foNum('000202'),
    referenceNumber: docType === 'final_offer' ? pqNum('000202') : undefined,
    issuedAt: '2026-08-27', validUntil: docType === 'final_offer' ? '2026-09-11' : undefined,
    seller: SELLER, buyer, items: [item], currency: 'USD',
    productAmount: item.amount, finishingAmount: item.finishing[0].amount,
    subtotal, freightAmount, total: subtotal + (freightAmount || 0),
    paymentTerms: docType === 'final_offer' ? '50% Deposit / 50% Before Shipment' : undefined,
    incoterm: 'FOB', namedPlace: 'Shanghai Port, China',
    leadTime: docType === 'final_offer' ? '30–35 days after sample approval' : undefined,
  };
}

// ============================================================================
// MANY PRODUCTS — 9 items (page break stress test)
// ============================================================================
function buildManyProducts(locale: DocumentLocale, docType: DocumentType): QuotationDocumentData {
  const buyer = locale === 'ko' ? BUYER_KO : locale === 'zh-CN' ? BUYER_ZH : BUYER_EN;

  type ManyItem = {
    id: string; sku: string; productName: string; capacity: string;
    productImageUrl: string; quantity: number; unitPrice: number; amount: number;
    finishing: { displayName: string; unitPrice: number; amount: number }[];
  };

  const ITEMS_EN: ManyItem[] = [
    { id: 'm1', sku: 'GB-AB-30', productName: 'Airless Pump Bottle 30 ml', capacity: '30 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Small%20airless%20pump%20bottle%2030ml%20transparent%20PETG%20cosmetic%20isolated%20on%20white%20background%20minimal%20product%20photography&width=160&height=160&seq=fix-many-en-1&orientation=squarish', quantity: 5000, unitPrice: 0.67, amount: 3350.00, finishing: [{ displayName: 'Silk Screen Printing', unitPrice: 0.07, amount: 350.00 }] },
    { id: 'm2', sku: 'GB-LB-50', productName: 'Lotion Pump Bottle 50 ml', capacity: '50 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Frosted%20white%20lotion%20pump%20bottle%2050ml%20cosmetic%20PP%20minimal%20white%20background%20studio%20product%20photography&width=160&height=160&seq=fix-many-en-2&orientation=squarish', quantity: 3000, unitPrice: 0.82, amount: 2460.00, finishing: [] },
    { id: 'm3', sku: 'GB-DR-30', productName: 'Serum Dropper 30 ml', capacity: '30 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Clear%20glass%20serum%20dropper%20bottle%2030ml%20cosmetic%20amber%20silver%20cap%20isolated%20white%20background%20minimal&width=160&height=160&seq=fix-many-en-3&orientation=squarish', quantity: 8000, unitPrice: 0.48, amount: 3840.00, finishing: [] },
    { id: 'm4', sku: 'GB-JR-50', productName: 'Cream Jar 50 ml', capacity: '50 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Glossy%20black%20cream%20jar%2050ml%20cosmetic%20PMMA%20luxury%20white%20background%20minimal%20product%20photography&width=160&height=160&seq=fix-many-en-4&orientation=squarish', quantity: 2000, unitPrice: 0.95, amount: 1900.00, finishing: [{ displayName: 'Silk Screen', unitPrice: 0.08, amount: 160.00 }] },
    { id: 'm5', sku: 'GB-LG-08', productName: 'Lip Gloss Tube 8 ml', capacity: '8 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Shiny%20pink%20lip%20gloss%20tube%208ml%20cosmetic%20clear%20ABS%20wand%20applicator%20isolated%20white%20background%20minimal&width=160&height=160&seq=fix-many-en-5&orientation=squarish', quantity: 10000, unitPrice: 0.32, amount: 3200.00, finishing: [] },
    { id: 'm6', sku: 'GB-CC-60', productName: 'Compact Case 60 mm', capacity: '-', productImageUrl: 'https://readdy.ai/api/search-image?query=Rose%20gold%20compact%20powder%20case%2060mm%20cosmetic%20makeup%20mirror%20inside%20minimal%20white%20background%20product%20photography&width=160&height=160&seq=fix-many-en-6&orientation=squarish', quantity: 4000, unitPrice: 1.20, amount: 4800.00, finishing: [{ displayName: 'Hot Stamp', unitPrice: 0.15, amount: 600.00 }] },
    { id: 'm7', sku: 'GB-FD-30', productName: 'Foundation Bottle 30 ml', capacity: '30 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Matte%20beige%20foundation%20dropper%20bottle%2030ml%20cosmetic%20minimal%20white%20background%20product%20photography%20clean&width=160&height=160&seq=fix-many-en-7&orientation=squarish', quantity: 6000, unitPrice: 0.88, amount: 5280.00, finishing: [] },
    { id: 'm8', sku: 'GB-EC-15', productName: 'Eye Cream Jar 15 ml', capacity: '15 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Small%20glass%20eye%20cream%20jar%2015ml%20luxury%20cosmetic%20white%20background%20minimal%20product%20photography%20silver%20lid&width=160&height=160&seq=fix-many-en-8&orientation=squarish', quantity: 3000, unitPrice: 1.10, amount: 3300.00, finishing: [] },
    { id: 'm9', sku: 'GB-TN-150', productName: 'Toner Pump 150 ml', capacity: '150 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Frosted%20transparent%20toner%20pump%20bottle%20150ml%20cosmetic%20skincare%20minimal%20white%20background%20product%20photography&width=160&height=160&seq=fix-many-en-9&orientation=squarish', quantity: 2500, unitPrice: 0.72, amount: 1800.00, finishing: [] },
  ];

  const ITEMS_KO: ManyItem[] = [
    { id: 'm1', sku: 'GB-AB-30', productName: '에어리스 펌프 보틀 30ml', capacity: '30 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Small%20airless%20pump%20bottle%2030ml%20transparent%20PETG%20cosmetic%20isolated%20on%20white%20background%20minimal%20product%20photography&width=160&height=160&seq=fix-many-ko-1&orientation=squarish', quantity: 5000, unitPrice: 0.67, amount: 3350.00, finishing: [{ displayName: '실크 인쇄', unitPrice: 0.07, amount: 350.00 }] },
    { id: 'm2', sku: 'GB-LB-50', productName: '로션 펌프 보틀 50ml', capacity: '50 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Frosted%20white%20lotion%20pump%20bottle%2050ml%20cosmetic%20PP%20minimal%20white%20background%20studio%20product%20photography&width=160&height=160&seq=fix-many-ko-2&orientation=squarish', quantity: 3000, unitPrice: 0.82, amount: 2460.00, finishing: [] },
    { id: 'm3', sku: 'GB-DR-30', productName: '세럼 드로퍼 30ml', capacity: '30 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Clear%20glass%20serum%20dropper%20bottle%2030ml%20cosmetic%20amber%20silver%20cap%20isolated%20white%20background%20minimal&width=160&height=160&seq=fix-many-ko-3&orientation=squarish', quantity: 8000, unitPrice: 0.48, amount: 3840.00, finishing: [] },
    { id: 'm4', sku: 'GB-JR-50', productName: '크림 자 50ml', capacity: '50 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Glossy%20black%20cream%20jar%2050ml%20cosmetic%20PMMA%20luxury%20white%20background%20minimal%20product%20photography&width=160&height=160&seq=fix-many-ko-4&orientation=squarish', quantity: 2000, unitPrice: 0.95, amount: 1900.00, finishing: [{ displayName: '실크 스크린', unitPrice: 0.08, amount: 160.00 }] },
    { id: 'm5', sku: 'GB-LG-08', productName: '립글로스 튜브 8ml', capacity: '8 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Shiny%20pink%20lip%20gloss%20tube%208ml%20cosmetic%20clear%20ABS%20wand%20applicator%20isolated%20white%20background%20minimal&width=160&height=160&seq=fix-many-ko-5&orientation=squarish', quantity: 10000, unitPrice: 0.32, amount: 3200.00, finishing: [] },
    { id: 'm6', sku: 'GB-CC-60', productName: '컴팩트 케이스 60mm', capacity: '-', productImageUrl: 'https://readdy.ai/api/search-image?query=Rose%20gold%20compact%20powder%20case%2060mm%20cosmetic%20makeup%20mirror%20inside%20minimal%20white%20background%20product%20photography&width=160&height=160&seq=fix-many-ko-6&orientation=squarish', quantity: 4000, unitPrice: 1.20, amount: 4800.00, finishing: [{ displayName: '핫 스탬프', unitPrice: 0.15, amount: 600.00 }] },
    { id: 'm7', sku: 'GB-FD-30', productName: '파운데이션 보틀 30ml', capacity: '30 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Matte%20beige%20foundation%20dropper%20bottle%2030ml%20cosmetic%20minimal%20white%20background%20product%20photography%20clean&width=160&height=160&seq=fix-many-ko-7&orientation=squarish', quantity: 6000, unitPrice: 0.88, amount: 5280.00, finishing: [] },
    { id: 'm8', sku: 'GB-EC-15', productName: '아이크림 자 15ml', capacity: '15 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Small%20glass%20eye%20cream%20jar%2015ml%20luxury%20cosmetic%20white%20background%20minimal%20product%20photography%20silver%20lid&width=160&height=160&seq=fix-many-ko-8&orientation=squarish', quantity: 3000, unitPrice: 1.10, amount: 3300.00, finishing: [] },
    { id: 'm9', sku: 'GB-TN-150', productName: '토너 펌프 150ml', capacity: '150 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Frosted%20transparent%20toner%20pump%20bottle%20150ml%20cosmetic%20skincare%20minimal%20white%20background%20product%20photography&width=160&height=160&seq=fix-many-ko-9&orientation=squarish', quantity: 2500, unitPrice: 0.72, amount: 1800.00, finishing: [] },
  ];

  const ITEMS_ZH: ManyItem[] = [
    { id: 'm1', sku: 'GB-AB-30', productName: '真空泵瓶 30ml', capacity: '30 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Small%20airless%20pump%20bottle%2030ml%20transparent%20PETG%20cosmetic%20isolated%20on%20white%20background%20minimal%20product%20photography&width=160&height=160&seq=fix-many-zh-1&orientation=squarish', quantity: 5000, unitPrice: 0.67, amount: 3350.00, finishing: [{ displayName: '丝网印刷', unitPrice: 0.07, amount: 350.00 }] },
    { id: 'm2', sku: 'GB-LB-50', productName: '乳液泵瓶 50ml', capacity: '50 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Frosted%20white%20lotion%20pump%20bottle%2050ml%20cosmetic%20PP%20minimal%20white%20background%20studio%20product%20photography&width=160&height=160&seq=fix-many-zh-2&orientation=squarish', quantity: 3000, unitPrice: 0.82, amount: 2460.00, finishing: [] },
    { id: 'm3', sku: 'GB-DR-30', productName: '精华滴管 30ml', capacity: '30 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Clear%20glass%20serum%20dropper%20bottle%2030ml%20cosmetic%20amber%20silver%20cap%20isolated%20white%20background%20minimal&width=160&height=160&seq=fix-many-zh-3&orientation=squarish', quantity: 8000, unitPrice: 0.48, amount: 3840.00, finishing: [] },
    { id: 'm4', sku: 'GB-JR-50', productName: '面霜广口瓶 50ml', capacity: '50 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Glossy%20black%20cream%20jar%2050ml%20cosmetic%20PMMA%20luxury%20white%20background%20minimal%20product%20photography&width=160&height=160&seq=fix-many-zh-4&orientation=squarish', quantity: 2000, unitPrice: 0.95, amount: 1900.00, finishing: [{ displayName: '丝网印刷', unitPrice: 0.08, amount: 160.00 }] },
    { id: 'm5', sku: 'GB-LG-08', productName: '唇彩管 8ml', capacity: '8 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Shiny%20pink%20lip%20gloss%20tube%208ml%20cosmetic%20clear%20ABS%20wand%20applicator%20isolated%20white%20background%20minimal&width=160&height=160&seq=fix-many-zh-5&orientation=squarish', quantity: 10000, unitPrice: 0.32, amount: 3200.00, finishing: [] },
    { id: 'm6', sku: 'GB-CC-60', productName: '粉饼盒 60mm', capacity: '-', productImageUrl: 'https://readdy.ai/api/search-image?query=Rose%20gold%20compact%20powder%20case%2060mm%20cosmetic%20makeup%20mirror%20inside%20minimal%20white%20background%20product%20photography&width=160&height=160&seq=fix-many-zh-6&orientation=squarish', quantity: 4000, unitPrice: 1.20, amount: 4800.00, finishing: [{ displayName: '烫金', unitPrice: 0.15, amount: 600.00 }] },
    { id: 'm7', sku: 'GB-FD-30', productName: '粉底液瓶 30ml', capacity: '30 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Matte%20beige%20foundation%20dropper%20bottle%2030ml%20cosmetic%20minimal%20white%20background%20product%20photography%20clean&width=160&height=160&seq=fix-many-zh-7&orientation=squarish', quantity: 6000, unitPrice: 0.88, amount: 5280.00, finishing: [] },
    { id: 'm8', sku: 'GB-EC-15', productName: '眼霜瓶 15ml', capacity: '15 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Small%20glass%20eye%20cream%20jar%2015ml%20luxury%20cosmetic%20white%20background%20minimal%20product%20photography%20silver%20lid&width=160&height=160&seq=fix-many-zh-8&orientation=squarish', quantity: 3000, unitPrice: 1.10, amount: 3300.00, finishing: [] },
    { id: 'm9', sku: 'GB-TN-150', productName: '爽肤水泵瓶 150ml', capacity: '150 ml', productImageUrl: 'https://readdy.ai/api/search-image?query=Frosted%20transparent%20toner%20pump%20bottle%20150ml%20cosmetic%20skincare%20minimal%20white%20background%20product%20photography&width=160&height=160&seq=fix-many-zh-9&orientation=squarish', quantity: 2500, unitPrice: 0.72, amount: 1800.00, finishing: [] },
  ];

  const items = locale === 'ko' ? ITEMS_KO : locale === 'zh-CN' ? ITEMS_ZH : ITEMS_EN;
  const productAmount = items.reduce((s, i) => s + i.amount, 0);
  const finishingAmount = items.reduce((s, i) => s + i.finishing.reduce((fs, f) => fs + f.amount, 0), 0);
  const subtotal = Math.round((productAmount + finishingAmount) * 100) / 100;
  const freightAmount = docType === 'final_offer' ? 1200 : undefined;

  return {
    documentType: docType, documentLocale: locale,
    quotationNumber: docType === 'preliminary_quote' ? pqNum('000203') : foNum('000203'),
    referenceNumber: docType === 'final_offer' ? pqNum('000203') : undefined,
    issuedAt: '2026-08-27', validUntil: docType === 'final_offer' ? '2026-09-11' : undefined,
    seller: SELLER, buyer, items, currency: 'USD',
    productAmount, finishingAmount, subtotal, freightAmount,
    total: subtotal + (freightAmount || 0),
    paymentTerms: docType === 'final_offer' ? '30% Deposit / 70% Before Shipment' : undefined,
    incoterm: 'FOB', namedPlace: 'Shanghai Port, China',
    leadTime: docType === 'final_offer' ? '30–40 days after artwork approval' : undefined,
  };
}

// ============================================================================
// LONG CONTENT — stress test with very long text
// ============================================================================
function buildLongContent(locale: DocumentLocale, docType: DocumentType): QuotationDocumentData {
  const buyer = locale === 'ko' ? {
    companyName: '롯데홈쇼핑앤케이뷰티인터내셔널주식회사',
    contactName: '오지현 글로벌소싱본부 시니어 바이어',
    address: '서울특별시 강남구 테헤란로 152, 강남파이낸스센터 20층 글로벌소싱팀',
    country: '대한민국',
    email: 'jihyun.oh.global.sourcing@lottehomeshopping.co.kr',
    phone: '+82-2-9999-7777',
  } : locale === 'zh-CN' ? {
    companyName: '上海联华日化商贸进出口有限责任公司',
    contactName: '刘伟强 采购部副总经理',
    address: '上海市浦东新区张江高科技园区博云路2号浦软大厦18层',
    country: '中国',
    email: 'liu.weiqiang@lianhua-trading.com.cn',
    phone: '+86-21-5050-8888',
  } : {
    companyName: 'Beautifully Yours International Cosmetics Wholesale & Distribution LLC',
    contactName: 'Alexandra Beaumont-Richardson, Senior Global Procurement Manager',
    address: '5000 Westheimer Road, Suite 1200, Houston, TX 77056, United States',
    country: 'United States of America',
    email: 'a.beaumont-richardson@beautifully-yours-intl.com',
    phone: '+1-713-555-0199',
  };

  type LcItem = {
    id: string; sku: string; productName: string;
    categoryL1Display: string; categoryL2Display: string;
    materialDisplay: string; capacity: string; specification: string;
    productImageUrl: string; quantity: number; unitPrice: number; amount: number;
    finishing: { displayName: string; color?: string; pantone?: string; unitPrice: number; amount: number }[];
  };

  const item_en: LcItem = {
    id: 'lc-1', sku: 'GB-SPEC-LONG-EN',
    productName: 'Premium Serum Glass Dropper Bottle Set — Limited Edition Custom Series (Matte Silver Metal Cap)',
    categoryL1Display: 'Premium Glass Packaging', categoryL2Display: 'Serum Line',
    materialDisplay: 'Borosilicate Glass Body / 304 Stainless Steel Pump / UV-Resistant Matte Spray Coating',
    capacity: '30 ml (±2% tolerance)', specification: 'Ø28mm × 140mm total height (incl. cap), Engineering Glass, Transmittance ≥92%',
    productImageUrl: 'https://readdy.ai/api/search-image?query=Premium%20glass%20dropper%20serum%20bottle%20luxury%20matte%20silver%20metal%20pump%20lid%20cosmetic%20packaging%20elegant%20minimal%20white%20background%20studio%20photography%20high%20end%20skincare&width=200&height=200&seq=fix-lc-en-1&orientation=squarish',
    quantity: 50000, unitPrice: 2.85, amount: 142500.00,
    finishing: [
      { displayName: 'Matte UV Offset Printing — 5-color CMYK + Gold/Silver Special (High-precision 140 LPI)', pantone: 'PANTONE 871 C + 877 C', unitPrice: 0.28, amount: 14000.00 },
      { displayName: 'Hot Stamp + Cold Foil Combo — Laser Metallic Transfer Film (0.025mm thickness)', color: 'Laser Gold + Laser Silver', unitPrice: 0.35, amount: 17500.00 },
      { displayName: 'Matte Hairline Primer Spray Coating (EU Food-Safety Certified Paint)', unitPrice: 0.22, amount: 11000.00 },
    ],
  };
  const item_ko: LcItem = {
    id: 'lc-1', sku: 'GB-SPEC-LONG-KO',
    productName: '프리미엄 세럼 글라스 드로퍼 보틀 세트 — 한정판 커스텀 시리즈 (무광 실버 메탈 캡)',
    categoryL1Display: '고급 유리 패키징', categoryL2Display: '세럼 라인',
    materialDisplay: '고붕규산유리 바디 / 304 스테인리스 스틸 펌프 / UV 방지 무광 스프레이 코팅',
    capacity: '30 ml (±2% 공차)', specification: '외경 28mm × 전체 높이 140mm (캡 포함), 특수 공학용 유리, 투광율 ≥92%',
    productImageUrl: 'https://readdy.ai/api/search-image?query=Premium%20glass%20dropper%20serum%20bottle%20luxury%20matte%20silver%20metal%20pump%20lid%20cosmetic%20packaging%20elegant%20minimal%20white%20background%20studio%20photography%20high%20end%20skincare&width=200&height=200&seq=fix-lc-ko-1&orientation=squarish',
    quantity: 50000, unitPrice: 2.85, amount: 142500.00,
    finishing: [
      { displayName: '무광 UV 평판인쇄 — 5색 CMYK + 전용 금/은 별색 (고정밀 140LPI)', pantone: 'PANTONE 871 C + 877 C', unitPrice: 0.28, amount: 14000.00 },
      { displayName: '핫 스탬핑/콜드 포일 복합공정 — 레이저 금속증착 필름 (0.025mm)', color: '레이저 골드+레이저 실버', unitPrice: 0.35, amount: 17500.00 },
      { displayName: '무광 헤어라인 기저도료 스프레이 코팅 (EU 식품안전 인증 도료)', unitPrice: 0.22, amount: 11000.00 },
    ],
  };
  const item_zh: LcItem = {
    id: 'lc-1', sku: 'GB-SPEC-LONG-ZH',
    productName: '高端精华液玻璃滴管瓶套组 — 限量版专属定制系列（磨砂银金属配件版）',
    categoryL1Display: '高档玻璃包装', categoryL2Display: '精华液系列',
    materialDisplay: '高硼硅玻璃瓶体 / 304不锈钢泵头 / 防紫外线磨砂喷涂',
    capacity: '30 ml（±2%容差）', specification: '外径28mm × 总高140mm（含盖），特种工程玻璃，透光率≥92%',
    productImageUrl: 'https://readdy.ai/api/search-image?query=Premium%20glass%20dropper%20serum%20bottle%20luxury%20matte%20silver%20metal%20pump%20lid%20cosmetic%20packaging%20elegant%20minimal%20white%20background%20studio%20photography%20high%20end%20skincare&width=200&height=200&seq=fix-lc-zh-1&orientation=squarish',
    quantity: 50000, unitPrice: 2.85, amount: 142500.00,
    finishing: [
      { displayName: '哑光UV平版印刷 — 5色CMYK+专色金银（高精度140LPI）', pantone: 'PANTONE 871 C + 877 C', unitPrice: 0.28, amount: 14000.00 },
      { displayName: '烫金烫银复合工艺 — 镭射镀铝转移膜（0.025mm）', color: '镭射金+镭射银', unitPrice: 0.35, amount: 17500.00 },
      { displayName: '磨砂哑光拉丝底漆喷涂（欧洲食品安全认证涂料）', unitPrice: 0.22, amount: 11000.00 },
    ],
  };

  const item = locale === 'ko' ? item_ko : locale === 'zh-CN' ? item_zh : item_en;
  const productAmount = item.amount;
  const finishingAmount = item.finishing.reduce((s, f) => s + f.amount, 0);
  const subtotal = productAmount + finishingAmount;
  const freightAmount = docType === 'final_offer' ? 3500 : undefined;
  const customsClearanceAmount = docType === 'final_offer' ? 850 : undefined;
  const total = subtotal + (freightAmount || 0) + (customsClearanceAmount || 0);

  const remarks = locale === 'zh-CN'
    ? '所有产品须符合欧盟化妆品包装法规（EC No 1223/2009）及中国国标GB/T 17447-2021。生产前须提供经客户签字确认的预生产样品。包装材料须出具SGS或BV机构的第三方检测报告。'
    : locale === 'ko'
    ? '모든 제품은 EU 화장품 포장 규정(EC No 1223/2009) 및 한국 식약처 기준을 준수해야 합니다. 양산 전 반드시 고객이 서명한 사전 생산 샘플을 제공해야 합니다. 포장재는 SGS 또는 BV 기관의 제3자 소재 안전 검사 보고서를 제출해야 합니다.'
    : 'All products must comply with EU Cosmetic Packaging Regulation (EC No 1223/2009). Pre-production samples (PP samples) signed by customer are mandatory before mass production. Third-party material safety test reports from SGS or BV must be provided with packaging materials.';

  return {
    documentType: docType, documentLocale: locale,
    quotationNumber: docType === 'preliminary_quote' ? pqNum('000204') : foNum('000204'),
    referenceNumber: docType === 'final_offer' ? pqNum('000204') : undefined,
    issuedAt: '2026-08-27', validUntil: docType === 'final_offer' ? '2026-09-11' : undefined,
    seller: SELLER, buyer, items: [item], currency: 'USD',
    productAmount, finishingAmount, subtotal, freightAmount, customsClearanceAmount, total,
    paymentTerms: docType === 'final_offer'
      ? (locale === 'ko' ? '계약금 30% / 선적 전 잔금 70% (T/T 전신환)' : locale === 'zh-CN' ? '预付款30% / 装船前付清70%（电汇T/T）' : '30% Deposit / 70% Before Shipment (T/T)')
      : undefined,
    incoterm: 'FOB', namedPlace: 'Shanghai Yangshan Port, China — Incoterms® 2020',
    leadTime: docType === 'final_offer'
      ? (locale === 'ko' ? '최종 아트워크 승인 후 35~45 영업일' : locale === 'zh-CN' ? '最终艺术稿确认后35-45个工作日' : '35–45 business days after final artwork approval')
      : undefined,
    remarks,
  };
}

// ============================================================================
// MANY FINISHINGS — 6 finishings on 1 product
// ============================================================================
function buildManyFinishings(locale: DocumentLocale, docType: DocumentType): QuotationDocumentData {
  const buyer = locale === 'ko' ? BUYER_KO : locale === 'zh-CN' ? BUYER_ZH : BUYER_EN;

  type FinItem = {
    id: string; sku: string; productName: string;
    materialDisplay: string; capacity: string;
    productImageUrl: string; quantity: number; unitPrice: number; amount: number;
    finishing: { displayName: string; color?: string; pantone?: string; description?: string; unitPrice: number; amount: number }[];
  };

  const FINISHINGS_EN = [
    { displayName: 'Matte UV Offset Printing (5-color CMYK + Special Gold)', color: 'CMYK + PMS 871 C', unitPrice: 0.28, amount: 5600.00 },
    { displayName: 'Hot Stamping (18K Gold Foil)', color: '18K Gold', pantone: 'PANTONE 871 C', unitPrice: 0.18, amount: 3600.00 },
    { displayName: 'Matte Hairline Primer Spray Coating', description: 'EU REACH Certified Paint', unitPrice: 0.15, amount: 3000.00 },
    { displayName: 'Laser Marking — Brand Logo Engraving', description: 'Precision ±0.05mm CO2 Laser', unitPrice: 0.08, amount: 1600.00 },
    { displayName: 'Chrome Electroplating (Pump & Cap)', color: 'Matte Chrome', unitPrice: 0.22, amount: 4400.00 },
    { displayName: 'Anti-Counterfeit Holographic Tamper-Evident Label', description: 'Variable QR Code Serial Number', unitPrice: 0.05, amount: 1000.00 },
  ];
  const FINISHINGS_KO = [
    { displayName: '무광 UV 평판인쇄 (5색 CMYK + 별색 금)', color: 'CMYK+PMS 871 C', unitPrice: 0.28, amount: 5600.00 },
    { displayName: '핫 스탬핑 (18K 골드 포일)', color: '18K 골드', pantone: 'PANTONE 871 C', unitPrice: 0.18, amount: 3600.00 },
    { displayName: '무광 헤어라인 기저도료', description: 'EU REACH 인증', unitPrice: 0.15, amount: 3000.00 },
    { displayName: '레이저 마킹 — 브랜드 로고', description: '정밀도 ±0.05mm CO2', unitPrice: 0.08, amount: 1600.00 },
    { displayName: '크롬 전기도금 (펌프+캡)', color: '무광 크롬', unitPrice: 0.22, amount: 4400.00 },
    { displayName: '위변조방지 홀로그램 라벨', description: '가변 QR코드 일련번호', unitPrice: 0.05, amount: 1000.00 },
  ];
  const FINISHINGS_ZH = [
    { displayName: '哑光UV平版印刷（5色CMYK+专色金）', color: 'CMYK+PMS 871 C', unitPrice: 0.28, amount: 5600.00 },
    { displayName: '烫金工艺（18K镀金）', color: '18K镀金', pantone: 'PANTONE 871 C', unitPrice: 0.18, amount: 3600.00 },
    { displayName: '哑光拉丝底漆喷涂', description: 'EU REACH认证涂料', unitPrice: 0.15, amount: 3000.00 },
    { displayName: '激光打标 — 品牌Logo', description: '精度±0.05mm CO2激光', unitPrice: 0.08, amount: 1600.00 },
    { displayName: '电镀铬工艺（泵头&盖子）', color: '哑铬', unitPrice: 0.22, amount: 4400.00 },
    { displayName: '防伪全息防篡改标签', description: '可变二维码序列号', unitPrice: 0.05, amount: 1000.00 },
  ];

  const item_en: FinItem = {
    id: 'mf-1', sku: 'GB-PREM-SET',
    productName: 'Premium Matte Pump Bottle 50 ml — Full Custom Edition',
    materialDisplay: 'Glossy PP Shell / ABS Inner / 304 Stainless Spring',
    capacity: '50 ml',
    productImageUrl: 'https://readdy.ai/api/search-image?query=Premium%20matte%20black%20pump%20bottle%2050ml%20cosmetic%20luxury%20full%20custom%20edition%20elegant%20dark%20minimal%20studio%20product%20photography%20white%20background&width=200&height=200&seq=fix-mf-en-1&orientation=squarish',
    quantity: 20000, unitPrice: 1.00, amount: 20000.00, finishing: FINISHINGS_EN,
  };
  const item_ko: FinItem = {
    id: 'mf-1', sku: 'GB-PREM-SET',
    productName: '프리미엄 무광 펌프 보틀 50ml — 풀 커스텀 에디션',
    materialDisplay: '유광 PP 쉘 / ABS 이너 / 304 스테인리스 스프링',
    capacity: '50 ml',
    productImageUrl: 'https://readdy.ai/api/search-image?query=Premium%20matte%20black%20pump%20bottle%2050ml%20cosmetic%20luxury%20full%20custom%20edition%20elegant%20dark%20minimal%20studio%20product%20photography%20white%20background&width=200&height=200&seq=fix-mf-ko-1&orientation=squarish',
    quantity: 20000, unitPrice: 1.00, amount: 20000.00, finishing: FINISHINGS_KO,
  };
  const item_zh: FinItem = {
    id: 'mf-1', sku: 'GB-PREM-SET',
    productName: '高端哑光泵瓶 50ml — 全套定制版',
    materialDisplay: '高光PP外壳 / ABS内胆 / 304不锈钢弹簧',
    capacity: '50 ml',
    productImageUrl: 'https://readdy.ai/api/search-image?query=Premium%20matte%20black%20pump%20bottle%2050ml%20cosmetic%20luxury%20full%20custom%20edition%20elegant%20dark%20minimal%20studio%20product%20photography%20white%20background&width=200&height=200&seq=fix-mf-zh-1&orientation=squarish',
    quantity: 20000, unitPrice: 1.00, amount: 20000.00, finishing: FINISHINGS_ZH,
  };

  const item = locale === 'ko' ? item_ko : locale === 'zh-CN' ? item_zh : item_en;
  const finishingAmount = item.finishing.reduce((s, f) => s + f.amount, 0);
  const subtotal = item.amount + finishingAmount;
  const freightAmount = docType === 'final_offer' ? 1100 : undefined;

  return {
    documentType: docType, documentLocale: locale,
    quotationNumber: docType === 'preliminary_quote' ? pqNum('000205') : foNum('000205'),
    referenceNumber: docType === 'final_offer' ? pqNum('000205') : undefined,
    issuedAt: '2026-08-27', validUntil: docType === 'final_offer' ? '2026-09-11' : undefined,
    seller: SELLER, buyer, items: [item], currency: 'USD',
    productAmount: item.amount, finishingAmount, subtotal, freightAmount,
    total: subtotal + (freightAmount || 0),
    paymentTerms: docType === 'final_offer' ? '30% Deposit / 70% Before Shipment' : undefined,
    incoterm: 'FOB', namedPlace: 'Shanghai Port, China',
    leadTime: docType === 'final_offer' ? '40–50 days after final artwork approval' : undefined,
  };
}

// ============================================================================
// LARGE NUMBERS — quantity 1,250,000 / total USD 1,234,567.89
// ============================================================================
function buildLargeNumbers(locale: DocumentLocale, docType: DocumentType): QuotationDocumentData {
  const buyer = locale === 'ko' ? BUYER_KO : locale === 'zh-CN' ? BUYER_ZH : BUYER_EN;

  type LnItem = {
    id: string; sku: string; productName: string; capacity: string;
    productImageUrl: string; quantity: number; unitPrice: number; amount: number;
    finishing: { displayName: string; unitPrice: number; amount: number }[];
  };

  const ITEMS_EN: LnItem[] = [
    {
      id: 'ln-1', sku: 'GB-VOL-BOTTLE', productName: 'High-Volume Lotion Bottle 200 ml (Bulk Custom)',
      capacity: '200 ml',
      productImageUrl: 'https://readdy.ai/api/search-image?query=Large%20volume%20lotion%20bottle%20200ml%20bulk%20cosmetic%20clear%20transparent%20white%20pump%20minimal%20studio%20product%20photography%20clean%20white%20background&width=200&height=200&seq=fix-ln-en-1&orientation=squarish',
      quantity: 1250000, unitPrice: 0.46, amount: 575000.00,
      finishing: [{ displayName: 'Silk Screen Printing (2 Colors)', unitPrice: 0.04, amount: 50000.00 }],
    },
    {
      id: 'ln-2', sku: 'GB-VOL-CAP', productName: 'Matching Magnetic Snap Cap (Bulk)',
      capacity: '-',
      productImageUrl: 'https://readdy.ai/api/search-image?query=Sleek%20magnetic%20snap%20cap%20cosmetic%20bottle%20closure%20chrome%20finish%20minimal%20white%20background%20product%20photography%20matte%20silver%20metallic&width=200&height=200&seq=fix-ln-en-2&orientation=squarish',
      quantity: 1250000, unitPrice: 0.18, amount: 225000.00,
      finishing: [],
    },
  ];

  const ITEMS_KO: LnItem[] = [
    {
      id: 'ln-1', sku: 'GB-VOL-BOTTLE', productName: '대용량 로션 보틀 200ml (대량 커스텀)',
      capacity: '200 ml',
      productImageUrl: 'https://readdy.ai/api/search-image?query=Large%20volume%20lotion%20bottle%20200ml%20bulk%20cosmetic%20clear%20transparent%20white%20pump%20minimal%20studio%20product%20photography%20clean%20white%20background&width=200&height=200&seq=fix-ln-ko-1&orientation=squarish',
      quantity: 1250000, unitPrice: 0.46, amount: 575000.00,
      finishing: [{ displayName: '실크 인쇄 (2색)', unitPrice: 0.04, amount: 50000.00 }],
    },
    {
      id: 'ln-2', sku: 'GB-VOL-CAP', productName: '매칭 마그네틱 캡 (대량)',
      capacity: '-',
      productImageUrl: 'https://readdy.ai/api/search-image?query=Sleek%20magnetic%20snap%20cap%20cosmetic%20bottle%20closure%20chrome%20finish%20minimal%20white%20background%20product%20photography%20matte%20silver%20metallic&width=200&height=200&seq=fix-ln-ko-2&orientation=squarish',
      quantity: 1250000, unitPrice: 0.18, amount: 225000.00,
      finishing: [],
    },
  ];

  const ITEMS_ZH: LnItem[] = [
    {
      id: 'ln-1', sku: 'GB-VOL-BOTTLE', productName: '高容量乳液瓶 200ml（批量定制）',
      capacity: '200 ml',
      productImageUrl: 'https://readdy.ai/api/search-image?query=Large%20volume%20lotion%20bottle%20200ml%20bulk%20cosmetic%20clear%20transparent%20white%20pump%20minimal%20studio%20product%20photography%20clean%20white%20background&width=200&height=200&seq=fix-ln-zh-1&orientation=squarish',
      quantity: 1250000, unitPrice: 0.46, amount: 575000.00,
      finishing: [{ displayName: '丝网印刷（2色）', unitPrice: 0.04, amount: 50000.00 }],
    },
    {
      id: 'ln-2', sku: 'GB-VOL-CAP', productName: '配套磁吸盖（批量）',
      capacity: '-',
      productImageUrl: 'https://readdy.ai/api/search-image?query=Sleek%20magnetic%20snap%20cap%20cosmetic%20bottle%20closure%20chrome%20finish%20minimal%20white%20background%20product%20photography%20matte%20silver%20metallic&width=200&height=200&seq=fix-ln-zh-2&orientation=squarish',
      quantity: 1250000, unitPrice: 0.18, amount: 225000.00,
      finishing: [],
    },
  ];

  const items = locale === 'ko' ? ITEMS_KO : locale === 'zh-CN' ? ITEMS_ZH : ITEMS_EN;
  const productAmount = items.reduce((s, i) => s + i.amount, 0);
  const finishingAmount = items.reduce((s, i) => s + i.finishing.reduce((fs, f) => fs + f.amount, 0), 0);
  const subtotal = Math.round((productAmount + finishingAmount) * 100) / 100;
  const freightAmount = docType === 'final_offer' ? 8500 : undefined;
  const customsClearanceAmount = docType === 'final_offer' ? 2200 : undefined;
  const total = subtotal + (freightAmount || 0) + (customsClearanceAmount || 0);

  return {
    documentType: docType, documentLocale: locale,
    quotationNumber: docType === 'preliminary_quote' ? pqNum('000206') : foNum('000206'),
    referenceNumber: docType === 'final_offer' ? pqNum('000206') : undefined,
    issuedAt: '2026-08-27', validUntil: docType === 'final_offer' ? '2026-09-11' : undefined,
    seller: SELLER, buyer, items, currency: 'USD',
    productAmount, finishingAmount, subtotal, freightAmount, customsClearanceAmount, total,
    exchangeRate: locale === 'ko' ? 1320.50 : locale === 'zh-CN' ? 7.24 : undefined,
    exchangeRateNote: locale === 'ko' ? 'USD 1 = KRW 1,320.50 (기준일: 2026-08-27)' : locale === 'zh-CN' ? 'USD 1 = CNY 7.24（截止日期：2026-08-27）' : undefined,
    paymentTerms: docType === 'final_offer' ? '25% Deposit / 75% Before Shipment (L/C accepted)' : undefined,
    incoterm: 'CIF', namedPlace: 'Long Beach Port, California, USA',
    leadTime: docType === 'final_offer' ? '45–60 days after artwork approval and L/C confirmation' : undefined,
    remarks: locale === 'zh-CN' ? '大批量采购优惠价格。货物分批装运，每批最小出货量500,000件。' : locale === 'ko' ? '대량 구매 특별 할인가. 분할 선적 가능, 최소 1회 출하량 500,000개.' : 'Volume discount pricing applied. Partial shipments available with minimum 500,000 pcs per shipment.',
  };
}

// ============================================================================
// Scenario registry
// ============================================================================
export type ScenarioKey = 'standard' | 'single_product' | 'many_products' | 'long_content' | 'many_finishings' | 'large_numbers';

export const SCENARIO_LABELS: Record<ScenarioKey, string> = {
  standard: 'Standard (3 Products)',
  single_product: 'Single Product',
  many_products: 'Many Products (9 items)',
  long_content: 'Long Content',
  many_finishings: 'Many Finishings (6)',
  large_numbers: 'Large Numbers',
};

export function getFixture(
  scenario: ScenarioKey,
  locale: DocumentLocale,
  docType: DocumentType,
): QuotationDocumentData {
  const builders: Record<ScenarioKey, (l: DocumentLocale, d: DocumentType) => QuotationDocumentData> = {
    standard: buildStandard,
    single_product: buildSingleProduct,
    many_products: buildManyProducts,
    long_content: buildLongContent,
    many_finishings: buildManyFinishings,
    large_numbers: buildLargeNumbers,
  };
  return builders[scenario](locale, docType);
}